/**
 * colors.ts - Corporate design color scheme
 * Last updated: 2025-08-12 08:46:45
 * Updated by: JulianPolly
 */

import React from 'react';  // Import React for CSSProperties type

// Corporate design colors
export const colors = {
    // Primary colors
    primary: "#8CD000", // PH Grün (hell)
    primaryLight: "#A6DE33", // PH Grün 65%
    primaryLighter: "#D8F099", // PH Grün 35%
    primaryVeryLight: "#ECF7CC", // PH Grün 15%

    darkGreen: "#267326", // Dunkelgrün RGB 38/115/38
    darkGreenLight: "#518F51", // Dunkelgrün 65%
    darkGreenLighter: "#9DBFA0", // Dunkelgrün 35%
    darkGreenVeryLight: "#DCEADD", // Dunkelgrün 15%

    // Neutral colors
    silver: "#BEBEB4", // PH Silber 100%
    silverLight: "#D1D1CA", // PH Silber 65%
    silverLighter: "#E4E4E1", // PH Silber 35%
    silverVeryLight: "#F3F3F2", // PH Silber 15%

    // Secondary colors
    sienna: "#96412D", // Sekundärfarbe Sienna
    siennaLight: "#B46F5F", // Sienna 65%
    siennaLighter: "#D3A69D", // Sienna 35%
    siennaVeryLight: "#EDDCD7", // Sienna 15%

    brillantBlue: "#0078A5", // Sekundärfarbe Brillantblau
    brillantBlueLight: "#3395B9", // Brillantblau 65%
    brillantBlueLighter: "#99C7DA", // Brillantblau 35%
    brillantBlueVeryLight: "#D5E9F2", // Brillantblau 15%

    lichtBlau: "#0FA5D7", // Sekundärfarbe Lichtblau
    lichtBlauLight: "#42B8E0", // Lichtblau 65%
    lichtBlauLighter: "#A1DCEF", // Lichtblau 35%
    lichtBlauVeryLight: "#D9F1F9", // Lichtblau 15%

    ocker: "#D77D00", // Sekundärfarbe Ocker
    ockerLight: "#E29933", // Ocker 65%
    ockerLighter: "#F0CC99", // Ocker 35%
    ockerVeryLight: "#F9EBDA", // Ocker 15%

    // Text colors
    textDark: "#333333",
    textMedium: "#555555",
    textLight: "#777777",

    // Background colors
    backgroundLight: "#F8F9FA", // Light background color (similar to original)
    backgroundMedium: "#EBEEF2", // Medium background color

    // Semantic colors
    success: "#8CD000", // Using PH Grün for success
    warning: "#D77D00", // Using Ocker for warnings
    error: "#96412D", // Using Sienna for errors
    info: "#0FA5D7", // Using Lichtblau for information

    // UI colors
    white: "#FFFFFF",
    black: "#000000",
};

export const sequenceGroupColors: Record<string, string> = {
    Testing: colors.darkGreen,           // Dunkelgrün (distinct for testing)
    Info: colors.silver,                 // PH Silber 100% (neutral info)
    A0: colors.primaryVeryLight,         // PH Grün 15% (very light green for A0)
    A1: colors.primary,                  // PH Grün (main green)
    A2: colors.brillantBlue,             // Brillantblau (distinct blue)
    A3: colors.sienna,                   // Sienna (warm, distinct)
};

// Chart color combinations
export const chartColors = {
    primary: [colors.lichtBlau, colors.darkGreen],
    alternative: [colors.primary, colors.brillantBlue],
    contrast: [colors.lichtBlau, colors.sienna]
};

// Common style objects - using React.CSSProperties type for proper typing
export const commonStyles: {
    container: React.CSSProperties;
    card: React.CSSProperties;
    socialInfluenceNotice: React.CSSProperties;
    heading: React.CSSProperties;
    subheading: React.CSSProperties;
    paragraph: React.CSSProperties;
    optionCard: {
        default: React.CSSProperties;
        selected: React.CSSProperties;
    };
    selectionCheck: React.CSSProperties;
} = {
    // Page container
    container: {
        maxWidth: 1250,
        margin: "0 auto",
        padding: "24px 20px 44px",
        backgroundColor: colors.backgroundLight, // Light background similar to original
    },

    // Cards
    card: {
        background: colors.white,
        boxShadow: "0 2px 12px rgba(0,0,0,0.09)",
        borderRadius: 14,
        padding: "24px 40px 28px",
        marginBottom: 28,
    },

    // Social influence notice
    socialInfluenceNotice: {
        backgroundColor: colors.ockerVeryLight,
        border: `1px solid ${colors.ockerLighter}`,
        padding: "14px 20px",
        borderRadius: 8,
        marginBottom: 22,
        fontSize: 16,
        fontWeight: 500,
    },

    // Typography
    heading: {
        color: colors.brillantBlue,
        fontWeight: 600,
        textAlign: "center",
    },

    subheading: {
        color: colors.brillantBlue,
        fontWeight: 600,
        textAlign: "center",
        fontSize: 24,
        marginBottom: 20,
    },

    paragraph: {
        fontSize: 17,
        lineHeight: 1.7,
        color: colors.textMedium,
    },

    // Option card states
    optionCard: {
        default: {
            flex: 1,
            border: `1px solid ${colors.silverLight}`,
            borderRadius: 14,
            padding: 24,
            backgroundColor: colors.white,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            cursor: "pointer",
            transition: "all 0.2s ease",
            position: "relative" as "relative", // Explicit type casting for position
        },
        selected: {
            flex: 1,
            border: `2px solid ${colors.lichtBlau}`,
            borderRadius: 14,
            padding: 24,
            backgroundColor: colors.lichtBlauVeryLight,
            boxShadow: `0 0 14px ${colors.lichtBlauLighter}`,
            cursor: "pointer",
            transition: "all 0.2s ease",
            position: "relative" as "relative", // Explicit type casting for position
        }
    },

    // Selection check
    selectionCheck: {
        position: "absolute" as "absolute", // Explicit type casting for position
        top: 16,
        right: 16,
        backgroundColor: colors.lichtBlau,
        color: colors.white,
        width: 28,
        height: 28,
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2
    }
};